import { isDefined } from '@sgme/fp';
import { BlotterDocuments } from 'components/shared/Blotter/BlotterPanel/blotter-panel-document/BlotterPanelDocument';
import { BlotterPanelActionButton } from 'components/shared/Blotter/BlotterPanel/BlotterPanelActionButton';
import { ConnectedScheduleMenu } from 'components/shared/Blotter/BlotterPanel/BlotterPanelFooter/ConnectedScheduleMenu';
import { FormattedMessage } from 'components/shared/Intl';
import { SpTooltip } from 'components/shared/Tooltip/Tooltip';
import { ImportantNotice } from 'components/TradeCancellation/ImportantNotice';
import { TradeCancellationBanner } from 'components/TradeCancellation/TradeCancellationBanner';
import { TradeCancellationModal } from 'components/TradeCancellation/TradeCancellationModal';
import { DocumentationV3 } from 'epic/common-form/Documentation';
import { useBlotterPanelContext } from 'hooks/useBlotterPanelContext';
import { useToggleFeature } from 'hooks/useToggleFeature';
import { useUserNotification } from 'hooks/useUserNotification';
import { IntlKey } from 'locales';
import type { BlotterItemAction } from 'models/blotters/BlotterItemAction';
import type { BlotterType } from 'models/blotters/BlotterType';
import type { IBlotterItem } from 'models/IBlotterItem';
import { ComponentProps, FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { BlotterPanelDocumentListLegacy } from '../blotter-panel-document-legacy/BlotterPanelDocumentListLegacy';

export interface BlotterPanelFooterOwnProps {
  blotterType: BlotterType;
  blotterItem: IBlotterItem;
}

export interface BlotterPanelFooterStateProps {
  actions: BlotterItemAction[];
  documents?: DocumentationV3;
}

interface BlotterPanelFooterActionTriggerProps {
  onAddonInitiated: () => void;
  onBatchReloadClicked: () => void;
  onCancellationWorkflowInitiated: () => void;
  onSecondaryReload(): void;
  onNewDynamicRepriceInitiated(): void;
  onNewStaticRepriceInitiated(): void;
  onProductModificationInitiated: () => void;
  onNewTradeAllocationInitiated: () => void;
}

export type BlotterPanelFooterDispatchProps = Omit<BlotterPanelFooterActionTriggerProps, 'onBatchReloadClicked'> & {
  onBatchReloadInitiated(newTabName: string): void;
};

export type BlotterPanelFooterProps = BlotterPanelFooterOwnProps &
  BlotterPanelFooterStateProps &
  BlotterPanelFooterDispatchProps;

const StyledComponentContainer = styled.div`
  .buttons-container {
    & > div {
      flex-basis: 45%;
      &:first-child:last-child,
      &:nth-child(odd):last-child {
        flex-basis: 100%;
      }
      &.trade-cancellation-button {
        flex-basis: auto;
      }
    }
    & > button {
      flex-basis: 45%;
      &:first-child:last-child,
      &:nth-child(odd):last-child {
        flex-basis: 100%;
      }
      &.trade-cancellation-button {
        flex-basis: auto;
      }
    }
  }
`;

export const BlotterPanelFooterComponent: FC<BlotterPanelFooterProps> = ({
  blotterItem,
  blotterType,
  actions,
  documents,
  onBatchReloadInitiated,
  ...actionTriggerProps
}) => {
  const isTradeCancellationEnabled = useToggleFeature('tradeCancellation');
  const isNextDocumentEnabled = useToggleFeature('nextDocumentParis');
  const { formatMessage } = useIntl();
  const notification = useUserNotification();
  const context = useBlotterPanelContext()!;
  const isMissedDealFeedback = context?.activeTab === 'MISSED_DEAL_FEEDBACK';
  const shouldDisplayFooter = useMemo(
    () =>
      !isMissedDealFeedback &&
      (
        ['trades', 'products', 'quotes', 'priceNegotiations', 'dlpProducts', 'dlpProductTrades'] as BlotterType[]
      ).includes(blotterType),
    [blotterType, isMissedDealFeedback],
  );

  const onBatchReloadClicked = useCallback(() => {
    onBatchReloadInitiated(
      `${formatMessage({
        id: `quote.products.${blotterItem.instrumentContractName}` as any,
      })} - ${formatMessage({ id: `batchPricer.tab.reload` })}`,
    );
    notification.success({
      title: { id: 'toast.reload' },
      content: [
        {
          id: 'blotterPanel.next.reload.batch.initiated',
          values: { quoteId: blotterItem.externalQuoteId ?? blotterItem.quoteId },
        },
      ],
    });
  }, [
    onBatchReloadInitiated,
    formatMessage,
    blotterItem.instrumentContractName,
    blotterItem.externalQuoteId,
    blotterItem.quoteId,
    notification,
  ]);

  const actionTriggers = {
    ...actionTriggerProps,
    onBatchReloadClicked: onBatchReloadClicked,
  };

  if (!shouldDisplayFooter) {
    return null;
  }

  return (
    <StyledComponentContainer className="card-footer container">
      {documents &&
        (isNextDocumentEnabled ? (
          <BlotterDocuments documents={documents} blotterItem={blotterItem} />
        ) : (
          <BlotterPanelDocumentListLegacy documents={documents} blotterItem={blotterItem} />
        ))}
      {isDefined(blotterItem.externalQuoteId) && (blotterType === 'quotes' || blotterType === 'trades') && (
        <ConnectedScheduleMenu
          quoteStatus={blotterItem.status}
          analyticContext={`${blotterType} blotter`}
          wrapperClassName="d-flex w-100 mt-2"
          btnClassName="btn-outline-secondary btn-xl flex-grow-1 "
          externalQuoteId={blotterItem.externalQuoteId}
          productSubType={blotterItem.instrumentContractName ?? blotterItem.productSubType}
          productType={blotterItem.instrumentName ?? blotterItem.productCode ?? blotterItem.productType}
          id={blotterType}
        />
      )}
      <div className="buttons-container d-flex flex-row flex-wrap  mt-3 gap-1">
        {actions.map(action => (
          <ActionButton key={action.type} action={action} actionTriggers={actionTriggers} blotterType={blotterType} />
        ))}
      </div>
      {blotterItem.blotterItemType === 'DlpProductTrade' && <TradeCancellationBanner />}
      {isTradeCancellationEnabled && <TradeCancellationModal />}
    </StyledComponentContainer>
  );
};

BlotterPanelFooterComponent.displayName = 'BlotterPanelFooterComponent';

type ActionButtonProps = {
  action: BlotterItemAction;
  actionTriggers: BlotterPanelFooterActionTriggerProps;
  blotterType: BlotterType;
};

function ActionButton({ action, actionTriggers, blotterType }: ActionButtonProps) {
  const { formatMessage } = useIntl();

  const buttonClassName = `btn btn-xl ${action.isDisabled ? 'btn-default' : 'btn-discreet-primary'} flex-grow-1 `;

  const tooltip = action.disabledReasonTranslateKey
    ? formatMessage({ id: action.disabledReasonTranslateKey as IntlKey })
    : undefined;

  switch (action.type) {
    case 'NewStaticReprice':
      return (
        <BlotterPanelActionButton
          id="quote-blotter-panel-new-static-screen-reprice"
          className={buttonClassName}
          onClick={actionTriggers.onNewStaticRepriceInitiated}
          isDisabled={action.isDisabled}
          label={`blotterPanel.actions.${action.translationKey}`}
          icon="open_in_new"
          tooltip={tooltip}
        />
      );
    case 'NewDynamicReprice':
      return (
        <BlotterPanelActionButton
          id="quote-blotter-panel-new-dynamic-screen-reprice"
          className={buttonClassName}
          onClick={actionTriggers.onNewDynamicRepriceInitiated}
          isDisabled={action.isDisabled}
          label={`blotterPanel.actions.${action.translationKey}`}
          icon="open_in_new"
          tooltip={tooltip}
        />
      );
    case 'OpenInSecondary':
      return (
        <BlotterPanelActionButton
          id="blotter-panel-open-in-secondary"
          className={buttonClassName}
          onClick={actionTriggers.onSecondaryReload}
          isDisabled={action.isDisabled}
          label={`blotterPanel.actions.${action.translationKey}`}
          icon="open_in_new"
        />
      );
    case 'BatchReprice':
      return (
        <BlotterPanelActionButton
          id="quote-blotter-panel-batch-screen-reprice"
          className={buttonClassName}
          onClick={actionTriggers.onBatchReloadClicked}
          isDisabled={action.isDisabled}
          label={`blotterPanel.actions.${action.translationKey}`}
          icon="open_in_new"
          tooltip={tooltip}
        />
      );
    case 'ProductModification':
      return (
        <BlotterPanelActionButton
          id="product-modification-button"
          className={`${buttonClassName}btn-icon-start`}
          onClick={actionTriggers.onProductModificationInitiated}
          isDisabled={action.isDisabled}
          label={`blotterPanel.actions.${action.translationKey}`}
          icon="create"
          iconStart={true}
        />
      );
    case 'AddOn':
      return (
        <BlotterPanelActionButton
          id="addon-button"
          className={`${buttonClassName}btn-icon-start`}
          onClick={actionTriggers.onAddonInitiated}
          isDisabled={action.isDisabled}
          label={`blotterPanel.actions.${action.translationKey}`}
          icon="tune"
          iconStart={true}
        />
      );
    case 'NewTradeAllocation':
      return (
        <button
          data-e2e="new-trade-allocation-button"
          className={`${buttonClassName}btn-icon-start`}
          type="button"
          onClick={actionTriggers.onNewTradeAllocationInitiated}
          disabled={action.isDisabled}
        >
          <i className="icon">pie_chart</i>
          <FormattedMessage id={`blotterPanel.actions.${action.translationKey}` as any} />
        </button>
      );
    case 'TradeCancellation':
      return (
        <TradeCancellationAction
          withTooltip={blotterType !== 'dlpProductTrades'}
          onClick={actionTriggers.onCancellationWorkflowInitiated}
          disabled={action.isDisabled}
        />
      );
    default:
      return null;
  }
}

type TradeCancellationActionProps = {
  withTooltip?: boolean;
} & ComponentProps<'button'>;

function TradeCancellationAction({ withTooltip = false, ...props }: TradeCancellationActionProps) {
  const { formatMessage } = useIntl();
  return (
    <SpTooltip position="top" contents={<ImportantNotice />} disabled={!withTooltip}>
      <div className="trade-cancellation-button">
        <button
          {...props}
          data-e2e="trade-cancellation-button"
          className="w-100 btn btn-xl btn-discreet-danger btn-icon flex-grow-auto"
          title={formatMessage({ id: 'blotterPanel.tooltip.TradeCancellation' })}
          type="button"
        >
          <i className="px-4 icon">cancel</i>
        </button>
      </div>
    </SpTooltip>
  );
}
